import { apiClient, type PageData } from './client'
import type { User } from './auth'

// ============================================
// 用户筛选参数
// ============================================
export interface UserFilterParams {
	username?: string
	nickname?: string
	email?: string
	phone?: string
	department_id?: number
	status?: number
	is_super_admin?: number
	start_date?: string
	end_date?: string
}

// ============================================
// 用户列表项（轻量字段）
// ============================================
export interface UserListItem {
	id: number
	username: string
	nickname: string
	phone: string
	email: string
	avatar: string
	status: number
	is_super_admin: boolean
	department_id: number | null
	crmeb_uid: number | null
	deposit_balance: number
	wechat: string
	gender: number
	created_at: string
	updated_at: string
	department_name?: string
}

// ============================================
// 用户列表分页响应
// ============================================
export interface UserListResponse {
	list: UserListItem[]
	total: number
	page: number
	page_size: number
}

// ============================================
// 员工整批转交结果计数
// ============================================
export interface UserTransferCounts {
	customers: number
	trackers: number
	gifts: number
	orders: number
}

// ============================================
// 用户选择器返回项
// ============================================
export interface UserSelectItem {
	id: number
	username: string
	nickname: string
	department_name: string
}

// ============================================
// 管理员编辑用户请求
// ============================================
export interface UpdateUserRequest {
	username?: string
	nickname?: string
	email?: string
	phone?: string
	avatar?: string
	department_id?: number | null
	status?: number
	password?: string
	wechat?: string
	gender?: number
	card_no?: string
	card_front?: string
	card_back?: string
	emergency_contact?: string
	emergency_mobile?: string
	address?: string
	remark?: string
	self_discipline?: string
}

// ============================================
// 用户管理 API
// ============================================
export const usersApi = {
	// 获取用户列表
	list: async (params?: {
		page?: number
		page_size?: number
	} & UserFilterParams): Promise<UserListResponse> => {
		const searchParams = new URLSearchParams()
		if (params?.page) searchParams.set('page', String(params.page))
		if (params?.page_size) searchParams.set('page_size', String(params.page_size))
		if (params?.username) searchParams.set('username', params.username)
		if (params?.nickname) searchParams.set('nickname', params.nickname)
		if (params?.email) searchParams.set('email', params.email)
		if (params?.phone) searchParams.set('phone', params.phone)
		if (params?.department_id) searchParams.set('department_id', String(params.department_id))
		if (params?.status !== undefined) searchParams.set('status', String(params.status))
		if (params?.is_super_admin !== undefined) searchParams.set('is_super_admin', String(params.is_super_admin))
		if (params?.start_date) searchParams.set('start_date', params.start_date)
		if (params?.end_date) searchParams.set('end_date', params.end_date)

		const response = await apiClient.get<UserListResponse>(`/users?${searchParams.toString()}`)
		return response.data
	},

	// 获取用户详情
	get: async (id: number): Promise<User> => {
		const response = await apiClient.get<User>(`/users/${id}`)
		return response.data
	},

	// 用户选择器列表
	selectUsers: async (params?: {
		page?: number
		page_size?: number
		keyword?: string
		department_id?: number
		user_ids?: number[] // 指定用户ID列表（用于回显已选用户）
	}): Promise<PageData<UserSelectItem>> => {
		const searchParams = new URLSearchParams()
		if (params?.page) searchParams.set('page', String(params.page))
		if (params?.page_size) searchParams.set('page_size', String(params.page_size))
		if (params?.keyword) searchParams.set('keyword', params.keyword)
		if (params?.department_id) searchParams.set('department_id', String(params.department_id))
		if (params?.user_ids && params.user_ids.length > 0) {
			searchParams.set('user_ids', params.user_ids.join(','))
		}

		const response = await apiClient.get<PageData<UserSelectItem>>(`/users/select?${searchParams.toString()}`)
		return response.data
	},

		// 管理员编辑用户
		update: async (id: number, data: UpdateUserRequest): Promise<void> => {
			await apiClient.put(`/users/${id}`, data)
		},

		// 切换用户状态
		updateStatus: async (id: number, status: number): Promise<void> => {
			await apiClient.put(`/users/${id}/status`, { status })
		},

		// 更新用户超管状态
		updateSuperAdmin: async (id: number, is_super_admin: boolean): Promise<void> => {
			await apiClient.put(`/users/${id}/super-admin`, { is_super_admin })
		},

		// 员工整批转交（客户锁定人/跟踪人、送礼记录、订单业绩）
		transfer: async (from_user_id: number, to_user_id: number): Promise<UserTransferCounts> => {
			const response = await apiClient.post<UserTransferCounts>('/users/transfer', { from_user_id, to_user_id })
			return response.data
		},
	}

/**
 * 将 UserSelectItem 转换为完整的 UserListItem
 */
export function userSelectItemToListItem(user: UserSelectItem): UserListItem {
	return {
		id: user.id,
		username: user.username,
		nickname: user.nickname,
		phone: '',
		email: '',
		avatar: '',
		status: 1,
		is_super_admin: false,
		department_id: null,
		crmeb_uid: null,
		deposit_balance: 0,
		wechat: '',
		gender: 0,
		created_at: '',
		updated_at: '',
		department_name: user.department_name,
	}
}

// 导出 User 类型供其他模块使用
export type { User }
